import React, { useState, useEffect } from "react";
import {
  getAllPromoCodes,
  getPromoCodeSettings,
  updatePromoCodeSettings,
  updatePromoCodeDiscount,
  updatePromoCodeReward,
  updatePromoCodeStatus,
  updatePromoCodesByRole,
  resetUserRewards,
  PromoCodeDTO,
  PromoCodeSettings,
} from "../services/promo-code-service";
import CustomInput from "../shared/custom-input/custom-input";
import { FaEdit, FaSave, FaTimes, FaCoins } from "react-icons/fa";

const AdminPromoCodeManagement: React.FC = () => {
  const [promoCodes, setPromoCodes] = useState<PromoCodeDTO[]>([]);
  const [settings, setSettings] = useState<PromoCodeSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingSettings, setEditingSettings] = useState(false);
  const [editingPromoId, setEditingPromoId] = useState<string | null>(null);
  const [newDefaultDiscount, setNewDefaultDiscount] = useState("");
  const [newDefaultCoins, setNewDefaultCoins] = useState("");
  const [editDiscount, setEditDiscount] = useState("");
  const [editReward, setEditReward] = useState("");
  const [savingPromoId, setSavingPromoId] = useState<string | null>(null);
  const [togglingPromoId, setTogglingPromoId] = useState<string | null>(null);
  const [bulkRole, setBulkRole] = useState("ROLE_STUDENT");
  const [bulkDiscount, setBulkDiscount] = useState("");
  const [bulkReward, setBulkReward] = useState("");
  const [bulkStatus, setBulkStatus] = useState<"none" | "activate" | "deactivate">("none");
  const [bulkLoading, setBulkLoading] = useState(false);
  const [resettingUserId, setResettingUserId] = useState<string | null>(null);
  
  // Pagination and filter states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [roleFilter, setRoleFilter] = useState<string>("all");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [codesData, settingsData] = await Promise.all([
        getAllPromoCodes(),
        getPromoCodeSettings(),
      ]);
      setPromoCodes(codesData);
      setSettings(settingsData);
      setNewDefaultDiscount(settingsData.defaultDiscountPercentage.toString());
      setNewDefaultCoins(settingsData.defaultCoinsReward.toString());
    } catch (error) {
      console.error("Failed to fetch promo code data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSettings = async () => {
    if (!settings) return;

    try {
      const updated = await updatePromoCodeSettings(
        parseFloat(newDefaultDiscount),
        parseInt(newDefaultCoins)
      );
      setSettings(updated);
      setEditingSettings(false);
      alert("Paramètres mis à jour avec succès !");
    } catch (error) {
      console.error("Failed to update settings:", error);
      alert("Échec de la mise à jour des paramètres");
    }
  };

  const handleSavePromo = async (promoCodeId: string) => {
    const target = promoCodes.find((pc) => pc.id === promoCodeId);
    if (!target) return;

    const discountValue = parseFloat(editDiscount);
    const rewardValue = parseInt(editReward, 10);

    if (Number.isNaN(discountValue) || Number.isNaN(rewardValue)) {
      alert("Veuillez entrer des valeurs valides pour la réduction et la récompense.");
      return;
    }

    setSavingPromoId(promoCodeId);
    try {
      let updated = target;
      if (discountValue !== target.discountPercentage) {
        updated = await updatePromoCodeDiscount(promoCodeId, discountValue);
      }
      if (rewardValue !== updated.coinsReward) {
        updated = await updatePromoCodeReward(promoCodeId, rewardValue);
      }

      setPromoCodes(
        promoCodes.map((pc) => (pc.id === promoCodeId ? updated : pc))
      );
      setEditingPromoId(null);
      setEditDiscount("");
      setEditReward("");
      alert("Code promo mis à jour avec succès !");
    } catch (error) {
      console.error("Failed to update promo code:", error);
      alert("Échec de la mise à jour du code promo");
    } finally {
      setSavingPromoId(null);
    }
  };

  const handleToggleStatus = async (promoCodeId: string, active: boolean) => {
    setTogglingPromoId(promoCodeId);
    try {
      const updated = await updatePromoCodeStatus(promoCodeId, active);
      setPromoCodes(
        promoCodes.map((pc) => (pc.id === promoCodeId ? updated : pc))
      );
    } catch (error) {
      console.error("Failed to update promo code status:", error);
      alert("Échec de la mise à jour du statut du code promo");
    } finally {
      setTogglingPromoId(null);
    }
  };

  const handleResetRewards = async (promo: PromoCodeDTO) => {
    const isTeacherRole =
      promo.ownerRole === "ROLE_TEACHER" || promo.ownerRole === "ROLE_SUPER_TEACHER";
    const confirmReset = window.confirm(
      `Remettre les récompenses à 0 pour ${promo.ownerName} ?` +
        (isTeacherRole ? " (Inclure les points enseignants ? Cliquez sur OK pour oui, Annuler pour non)" : "")
    );
    if (!confirmReset) return;

    const resetTeacherPoints = isTeacherRole ? true : false;
    setResettingUserId(promo.ownerId);
    try {
      await resetUserRewards(promo.ownerId, resetTeacherPoints);
      alert("Récompenses remises à zéro.");
    } catch (error) {
      console.error("Failed to reset rewards:", error);
      alert("Échec de la remise à zéro des récompenses");
    } finally {
      setResettingUserId(null);
    }
  };

  const handleBulkUpdate = async () => {
    const discountValue = bulkDiscount.trim() === "" ? undefined : parseFloat(bulkDiscount);
    const rewardValue = bulkReward.trim() === "" ? undefined : parseInt(bulkReward, 10);
    const activeValue =
      bulkStatus === "none" ? undefined : bulkStatus === "activate" ? true : false;

    if (
      discountValue === undefined &&
      rewardValue === undefined &&
      activeValue === undefined
    ) {
      alert("Veuillez définir au moins un champ (réduction, récompense ou statut) à mettre à jour.");
      return;
    }

    setBulkLoading(true);
    try {
      const updated = await updatePromoCodesByRole(
        bulkRole,
        discountValue,
        rewardValue,
        activeValue
      );

      if (updated && updated.length > 0) {
        const updatedMap = new Map(updated.map((pc) => [pc.id, pc]));
        setPromoCodes((prev) =>
          prev.map((pc) => (updatedMap.has(pc.id) ? (updatedMap.get(pc.id) as PromoCodeDTO) : pc))
        );
      }
      alert("Mise à jour globale appliquée avec succès !");
    } catch (error) {
      console.error("Failed to bulk update promo codes:", error);
      alert("Échec de la mise à jour globale des codes promo");
    } finally {
      setBulkLoading(false);
    }
  };

  // Filter and pagination logic
  const filteredPromoCodes = promoCodes.filter((promo) => {
    if (roleFilter === "all") return true;
    return promo.ownerRole === roleFilter;
  });

  const totalPages = Math.ceil(filteredPromoCodes.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedPromoCodes = filteredPromoCodes.slice(startIndex, endIndex);


  const roleLabels: Record<string, string> = {
    ROLE_TEACHER: "Professeur",
    ROLE_STUDENT: "Eleve",
    ROLE_ADMIN: "Administrateur",
    ROLE_SUPER_TEACHER: "Super Professeur",
  };

  // Get unique roles from promo codes (technical)
  const availableRoles = Array.from(
    new Set(promoCodes.map((promo) => promo.ownerRole).filter(Boolean))
  );

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [roleFilter]);

  if (loading) {
    return (
      <div className="p-6">
        <p>Chargement des données des codes promo...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 admin-promo-management bg-gray-50">
      <h1 className="mb-6 text-3xl font-bold text-gray-800">
        Gestion des codes promo
      </h1>

      {/* Section des paramètres */}
      <div className="p-6 mb-6 bg-white rounded-lg shadow-md">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-800">
            Paramètres par défaut
          </h2>
          {!editingSettings && (
            <button
              onClick={() => setEditingSettings(true)}
              className="flex items-center gap-2 px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              {(FaEdit as any)({ className: "inline-block" })} Modifier
            </button>
          )}
        </div>

        {settings && (
          <div className="space-y-4">
            {editingSettings ? (
              <>
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Pourcentage de réduction par défaut
                  </label>
                  <CustomInput
                    inputType="number"
                    value={newDefaultDiscount}
                    onChange={(e) => setNewDefaultDiscount(e.target.value)}
                    placeholder="ex: 10"
                  />
                </div>
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Récompense de pièces par défaut
                  </label>
                  <CustomInput
                    inputType="number"
                    value={newDefaultCoins}
                    onChange={(e) => setNewDefaultCoins(e.target.value)}
                    placeholder="ex: 100"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleUpdateSettings}
                    className="flex items-center gap-2 px-4 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700"
                  >
                    {(FaSave as any)({ className: "inline-block" })} Enregistrer
                  </button>
                  <button
                    onClick={() => {
                      setEditingSettings(false);
                      setNewDefaultDiscount(
                        settings.defaultDiscountPercentage.toString()
                      );
                      setNewDefaultCoins(settings.defaultCoinsReward.toString());
                    }}
                    className="flex items-center gap-2 px-4 py-2 text-white bg-gray-500 rounded-lg hover:bg-gray-600"
                  >
                    {(FaTimes as any)({ className: "inline-block" })} Annuler
                  </button>
                </div>
              </>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-blue-50">
                  <p className="mb-1 text-sm text-gray-600">Réduction par défaut</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {settings.defaultDiscountPercentage}%
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-yellow-50">
                  <p className="mb-1 text-sm text-gray-600">Récompense de pièces par défaut</p>
                  <p className="flex items-center gap-2 text-2xl font-bold text-yellow-600">
                    {(FaCoins as any)({ className: "inline-block" })} {settings.defaultCoinsReward}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bulk update by role */}
      <div className="p-6 mb-6 bg-white rounded-lg shadow-md">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-800">Mise à jour globale par rôle</h2>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">Rôle</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={bulkRole}
              onChange={(e) => setBulkRole(e.target.value)}
            >
              <option value="ROLE_STUDENT">Élèves</option>
              <option value="ROLE_TEACHER">Professeurs</option>
              <option value="ROLE_SUPER_TEACHER">Super Professeurs</option>
            </select>
          </div>
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">Réduction (%)</label>
            <CustomInput
              inputType="number"
              value={bulkDiscount}
              onChange={(e) => setBulkDiscount(e.target.value)}
              placeholder="Laisser vide pour conserver"
            />
          </div>
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">Récompense</label>
            <CustomInput
              inputType="number"
              value={bulkReward}
              onChange={(e) => setBulkReward(e.target.value)}
              placeholder="Laisser vide pour conserver"
            />
          </div>
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">Statut</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={bulkStatus}
              onChange={(e) => setBulkStatus(e.target.value as any)}
            >
              <option value="none">Ne pas changer</option>
              <option value="activate">Activer tous</option>
              <option value="deactivate">Désactiver tous</option>
            </select>
          </div>
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">&nbsp;</label>
            <button
              onClick={handleBulkUpdate}
              disabled={bulkLoading}
              className={`w-full px-6 py-2 text-white font-semibold rounded-lg transition-colors shadow-md ${bulkLoading ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-primary to-primary_bg hover:from-primary_bg hover:to-primary"}`}
            >
              {bulkLoading ? "Application..." : "Appliquer"}
            </button>
          </div>
        </div>
      </div>

      {/* Liste des codes promo */}
      <div className="p-6 bg-white rounded-lg shadow-md">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-800">
            Tous les codes promo ({filteredPromoCodes.length})
          </h2>
          
          {/* Role Filter */}
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700">
              Filtrer par rôle:
            </label>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tous les rôles</option>
              {availableRoles.map((role) => (
                <option key={role} value={role}>
                  {roleLabels[role] || role}
                </option>
              ))}
            </select>
            
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="5">5 par page</option>
              <option value="10">10 par page</option>
              <option value="20">20 par page</option>
              <option value="50">50 par page</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-100 border-b">
              <tr>
                <th className="px-4 py-3 text-sm font-semibold text-left text-gray-700">Code</th>
                <th className="px-4 py-3 text-sm font-semibold text-left text-gray-700">Propriétaire</th>
                <th className="px-4 py-3 text-sm font-semibold text-left text-gray-700">Rôle</th>
                <th className="px-4 py-3 text-sm font-semibold text-left text-gray-700">Réduction</th>
                <th className="px-4 py-3 text-sm font-semibold text-left text-gray-700">Récompense</th>
                <th className="px-4 py-3 text-sm font-semibold text-left text-gray-700">Utilisation</th>
                <th className="px-4 py-3 text-sm font-semibold text-left text-gray-700">NJE7EK Coins</th>
                <th className="px-4 py-3 text-sm font-semibold text-left text-gray-700">Statut</th>
                <th className="px-4 py-3 text-sm font-semibold text-left text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedPromoCodes.map((promo) => (
                <tr key={promo.id} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono font-bold text-blue-600">
                    {promo.code}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {promo.ownerName}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    <span className="px-2 py-1 text-xs font-medium text-purple-800 bg-purple-100 rounded">
                      {roleLabels[promo.ownerRole] || promo.ownerRole || "N/A"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {editingPromoId === promo.id ? (
                      <div className="flex items-center gap-2">
                        <CustomInput
                          inputType="number"
                          value={editDiscount}
                          onChange={(e) => setEditDiscount(e.target.value)}
                          CustomStyle="w-20"
                        />
                        <span className="text-sm">%</span>
                      </div>
                    ) : (
                      <span className="font-semibold text-green-600">
                        {promo.discountPercentage}%
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {editingPromoId === promo.id ? (
                      <div className="flex items-center gap-2">
                        <CustomInput
                          inputType="number"
                          value={editReward}
                          onChange={(e) => setEditReward(e.target.value)}
                          CustomStyle="w-20"
                        />
                        <span className="text-sm text-gray-600">pièces</span>
                      </div>
                    ) : (
                      <span className="flex items-center gap-1 font-semibold text-amber-600">
                        {(FaCoins as any)({ className: "inline-block" })}
                        {promo.coinsReward}
                      </span>
                    )}
                  </td>
                <td className="px-4 py-3 text-sm text-gray-700">
                  {promo.usageCount} fois
                </td>
                <td className="px-4 py-3 text-sm text-gray-700">
                  <span className="flex items-center gap-1 font-semibold text-blue-700">
                    {(FaCoins as any)({ className: "inline-block" })}
                    {promo.ownerCoins ?? 0}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span
                      className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        promo.isActive
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {promo.isActive ? "Actif" : "Inactif"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {editingPromoId === promo.id ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSavePromo(promo.id)}
                          className={`text-green-600 hover:text-green-800 ${savingPromoId === promo.id ? "opacity-50 cursor-not-allowed" : ""}`}
                          title="Enregistrer"
                          disabled={savingPromoId === promo.id}
                        >
                          {(FaSave as any)({})}
                        </button>
                        <button
                          onClick={() => {
                            setEditingPromoId(null);
                            setEditDiscount("");
                            setEditReward("");
                          }}
                          className="text-red-600 hover:text-red-800"
                          title="Annuler"
                        >
                          {(FaTimes as any)({})}
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => {
                            setEditingPromoId(promo.id);
                            setEditDiscount(promo.discountPercentage.toString());
                            setEditReward(promo.coinsReward.toString());
                          }}
                          className="text-blue-600 hover:text-blue-800"
                          title="Modifier le code promo"
                        >
                          {(FaEdit as any)({})}
                        </button>
                        <button
                          onClick={() => handleToggleStatus(promo.id, !promo.isActive)}
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            promo.isActive
                              ? "bg-red-100 text-red-700 hover:bg-red-200"
                              : "bg-green-100 text-green-700 hover:bg-green-200"
                          } ${togglingPromoId === promo.id ? "opacity-50 cursor-not-allowed" : ""}`}
                          disabled={togglingPromoId === promo.id}
                        >
                          {promo.isActive ? "Bloquer" : "Débloquer"}
                        </button>
                        <button
                          onClick={() => handleResetRewards(promo)}
                          className={`px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700 hover:bg-orange-200 ${
                            resettingUserId === promo.ownerId ? "opacity-50 cursor-not-allowed" : ""
                          }`}
                          disabled={resettingUserId === promo.ownerId}
                        >
                          Réinitialiser
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6">
            <div className="text-sm text-gray-600">
              Affichage de {startIndex + 1} à {Math.min(endIndex, filteredPromoCodes.length)} sur {filteredPromoCodes.length} codes
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Premier
              </button>
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Précédent
              </button>
              
              <div className="flex items-center gap-2">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((page) => {
                    // Show first page, last page, current page, and pages around current
                    return (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    );
                  })
                  .map((page, index, array) => (
                    <React.Fragment key={page}>
                      {index > 0 && array[index - 1] !== page - 1 && (
                        <span className="px-2">...</span>
                      )}
                      <button
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-2 rounded-lg ${
                          currentPage === page
                            ? "bg-blue-600 text-white"
                            : "border border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  ))}
              </div>

              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Suivant
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Dernier
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPromoCodeManagement;


